import { useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';
import { platform } from '@/utils/platform';
import { nativeGoogleSignIn, isNativeGoogleAuthAvailable } from '@/capacitor/plugins/google-auth';
import { useAuth } from '../hooks/useAuth';

export interface GoogleSignInButtonProps {
  onSuccess: (credential: string) => void;
  onError?: (error: Error) => void;
}

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const buttonStyle = {
  backgroundColor: 'rgba(255,255,255,0.15)',
  border: '1px solid rgba(255,255,255,0.2)',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  color: '#0f172a',
} as const;

/**
 * Native Google Sign-In button — uses @capgo/capacitor-social-login SDK.
 * Rendered only on native platforms (no useGoogleLogin hook).
 */
function NativeGoogleSignInButton({ onError }: GoogleSignInButtonProps) {
  const { t } = useTranslation();
  const { googleSignIn } = useAuth();
  const [loading, setLoading] = useState(false);

  if (!isNativeGoogleAuthAvailable()) return null;

  const handleClick = async () => {
    try {
      setLoading(true);
      const idToken = await nativeGoogleSignIn();
      googleSignIn(idToken);
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error('Native Google sign-in failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className="glass-btn w-full flex items-center justify-center gap-3 rounded-xl py-3 px-4 text-sm font-bold transition-all active:scale-[0.98] cursor-pointer disabled:opacity-60"
        style={buttonStyle}
      >
        <GoogleIcon />
        {t('auth.signInWithGoogle', 'Sign in with Google')}
      </button>
    </div>
  );
}

/**
 * Web Google Sign-In button — uses @react-oauth/google auth-code redirect flow.
 * Only rendered on web where GoogleOAuthProvider is present.
 */
function WebGoogleSignInButton({ onError }: GoogleSignInButtonProps) {
  const { t } = useTranslation();

  const webLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: `${window.location.origin}/auth/callback`,
    onError: (errorResponse) => {
      onError?.(new Error(errorResponse.error_description || 'Google sign-in failed'));
    },
  });

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => webLogin()}
        className="glass-btn w-full flex items-center justify-center gap-3 rounded-xl py-3 px-4 text-sm font-bold transition-all active:scale-[0.98] cursor-pointer disabled:opacity-60"
        style={buttonStyle}
      >
        <GoogleIcon />
        {t('auth.signInWithGoogle', 'Sign in with Google')}
      </button>
    </div>
  );
}

/**
 * Google Sign-In button — dual web/native flow.
 * Web: auth-code redirect flow via @react-oauth/google (requires GoogleOAuthProvider).
 * Native: native Google Sign-In SDK via @capgo/capacitor-social-login.
 *
 * Split into two components to avoid calling useGoogleLogin on native
 * where GoogleOAuthProvider is not mounted (it throws {}).
 */
export const GoogleSignInButton = (props: GoogleSignInButtonProps) => {
  if (platform.isNative) return <NativeGoogleSignInButton {...props} />;
  return <WebGoogleSignInButton {...props} />;
};
